import { z } from 'zod';

import {
  SITE_BRAND_TITLE,
  type StaticPageDefinition,
} from '../config/static-pages.ts';
import type { WritingCategory } from './registry.ts';
import { WRITING_CATEGORIES } from './registry.ts';
import {
  seoArtifactSchema,
  type PublicProjectRecord,
  type PublicWritingRecord,
} from './schemas.ts';

export type SeoArtifact = z.infer<typeof seoArtifactSchema>;

export type RobotsDirective = 'index,follow' | 'noindex,nofollow';

const DEFAULT_SITE_ORIGIN = 'https://example.com';

export function resolveSiteOrigin(
  env: Record<string, string | undefined> = process.env
): string {
  const raw = env.VITE_SITE_URL?.trim();
  if (!raw) {
    return DEFAULT_SITE_ORIGIN;
  }
  const url = new URL(raw);
  return url.origin;
}

export function normalizeRoutePath(routePath: string): string {
  if (!routePath.startsWith('/')) {
    throw new Error(`Route paths must be root-relative: ${routePath}`);
  }
  if (routePath.length > 1 && routePath.endsWith('/')) {
    throw new Error(`Route paths must not have trailing slashes: ${routePath}`);
  }
  return routePath === '/' ? '/' : routePath;
}

export function buildAbsoluteUrl(
  routeOrAssetPath: string,
  siteOrigin: string
): string {
  const normalizedPath = normalizeRoutePath(routeOrAssetPath);
  return new URL(normalizedPath, siteOrigin).toString();
}

export function buildDocumentTitle(
  primaryTitle: string,
  options: { includeBrandSuffix?: boolean } = {}
): string {
  if (options.includeBrandSuffix === false) {
    return primaryTitle;
  }
  if (primaryTitle === SITE_BRAND_TITLE) {
    return primaryTitle;
  }
  return `${primaryTitle} — ${SITE_BRAND_TITLE}`;
}

function parseSeoArtifact(value: unknown, context: string): SeoArtifact {
  const parsed = seoArtifactSchema.safeParse(value);
  if (!parsed.success) {
    throw new Error(
      `${context}: ${parsed.error.issues.map((issue) => issue.message).join('; ')}`
    );
  }
  return parsed.data;
}

export function buildStaticPageSeo(
  page: StaticPageDefinition,
  siteOrigin: string
): SeoArtifact {
  const canonicalUrl = buildAbsoluteUrl(page.path, siteOrigin);
  const openGraphUrl =
    page.path === '/attribution'
      ? buildAbsoluteUrl('/', siteOrigin)
      : canonicalUrl;

  return parseSeoArtifact(
    {
      title: page.title,
      description: page.description,
      canonicalUrl,
      robots: 'index,follow' satisfies RobotsDirective,
      openGraph: {
        type: page.openGraph.type,
        title: page.openGraph.title,
        description: page.openGraph.description,
        url: openGraphUrl,
      },
      twitter: {
        card: 'summary_large_image',
        title: page.twitter.title,
        description: page.twitter.description,
      },
      sitemap: page.sitemap,
    },
    `static page ${page.path}`
  );
}

export function buildProjectDetailSeo(
  project: PublicProjectRecord,
  siteOrigin: string
): SeoArtifact {
  if (project.detailStatus !== 'case-study' || !project.route) {
    throw new Error(
      `Project ${project.slug} is not a published case study route`
    );
  }

  const canonicalUrl = buildAbsoluteUrl(project.route, siteOrigin);
  const image = buildAbsoluteUrl(project.hero.src, siteOrigin);
  const documentTitle = buildDocumentTitle(project.title);

  return parseSeoArtifact(
    {
      title: documentTitle,
      description: project.description,
      canonicalUrl,
      robots: 'index,follow',
      openGraph: {
        type: 'article',
        title: project.title,
        description: project.description,
        url: canonicalUrl,
        image,
      },
      twitter: {
        card: 'summary_large_image',
        title: documentTitle,
        description: project.description,
        image,
      },
      sitemap: {
        include: true,
        priority: 0.7,
        lastModified: project.publishedAt,
      },
    },
    `project ${project.slug}`
  );
}

export function buildWritingDetailSeo(
  writing: PublicWritingRecord,
  siteOrigin: string
): SeoArtifact {
  const canonicalUrl = buildAbsoluteUrl(writing.route, siteOrigin);
  const image = buildAbsoluteUrl(writing.hero.src, siteOrigin);
  const documentTitle = buildDocumentTitle(writing.title);

  return parseSeoArtifact(
    {
      title: documentTitle,
      description: writing.description,
      canonicalUrl,
      robots: 'index,follow',
      openGraph: {
        type: 'article',
        title: writing.title,
        description: writing.description,
        url: canonicalUrl,
        image,
      },
      twitter: {
        card: 'summary_large_image',
        title: documentTitle,
        description: writing.description,
        image,
      },
      sitemap: {
        include: true,
        priority: 0.7,
        lastModified: writing.publishedAt,
      },
    },
    `writing ${writing.slug}`
  );
}

const CATEGORY_COPY: Record<
  WritingCategory,
  { title: string; description: string; openGraphTitle: string }
> = {
  engineering: {
    title: `Engineering — Blog — ${SITE_BRAND_TITLE}`,
    description:
      'Engineering notes, architecture decisions, and lessons from building production systems.',
    openGraphTitle: 'Engineering posts',
  },
  life: {
    title: `Life — Blog — ${SITE_BRAND_TITLE}`,
    description:
      'Retrospectives, how I see the world, remote work reflections, and the occasional travel note.',
    openGraphTitle: 'Life posts',
  },
  tech: {
    title: `Tech — Blog — ${SITE_BRAND_TITLE}`,
    description:
      'Engineering deep dives, tutorials, mental models, and lessons from building distributed systems.',
    openGraphTitle: 'Tech posts',
  },
};

export function buildCategorySeo(
  category: WritingCategory,
  siteOrigin: string
): SeoArtifact {
  if (!WRITING_CATEGORIES.includes(category)) {
    throw new Error(`Unsupported writing category: ${category}`);
  }

  const route = `/blog/category/${category}`;
  const canonicalUrl = buildAbsoluteUrl(route, siteOrigin);
  const copy = CATEGORY_COPY[category];

  return parseSeoArtifact(
    {
      title: copy.title,
      description: copy.description,
      canonicalUrl,
      robots: 'index,follow',
      openGraph: {
        type: 'website',
        title: copy.openGraphTitle,
        description: copy.description,
        url: canonicalUrl,
      },
      twitter: {
        card: 'summary_large_image',
        title: copy.title,
        description: copy.description,
      },
      sitemap: {
        include: true,
        priority: 0.6,
      },
    },
    `category ${category}`
  );
}

export function serializeSeoForComparison(artifact: SeoArtifact) {
  return {
    title: artifact.title,
    description: artifact.description,
    canonicalUrl: artifact.canonicalUrl,
    robots: artifact.robots,
    openGraph: {
      ...artifact.openGraph,
      url: new URL(artifact.openGraph.url).pathname,
      image: artifact.openGraph.image
        ? new URL(artifact.openGraph.image).pathname
        : undefined,
    },
    twitter: {
      ...artifact.twitter,
      image: artifact.twitter.image
        ? new URL(artifact.twitter.image).pathname
        : undefined,
    },
    sitemap: artifact.sitemap,
  };
}

export function buildSitemapEntries(
  artifacts: Array<{ path: string; seo: SeoArtifact }>,
  siteOrigin: string
) {
  return artifacts
    .filter((entry) => entry.seo.sitemap.include)
    .map((entry) => ({
      loc: buildAbsoluteUrl(entry.path, siteOrigin),
      lastmod: entry.seo.sitemap.lastModified,
      priority: entry.seo.sitemap.priority,
    }))
    .sort((left, right) => left.loc.localeCompare(right.loc));
}

export function renderSitemapXml(
  entries: Array<{ loc: string; lastmod?: string; priority?: number }>
): string {
  const urlNodes = entries
    .map((entry) => {
      const lines = [`    <loc>${escapeXml(entry.loc)}</loc>`];
      if (entry.lastmod) {
        lines.push(`    <lastmod>${escapeXml(entry.lastmod)}</lastmod>`);
      }
      if (typeof entry.priority === 'number') {
        lines.push(`    <priority>${entry.priority.toFixed(1)}</priority>`);
      }
      return `  <url>\n${lines.join('\n')}\n  </url>`;
    })
    .join('\n');

  return `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n${urlNodes}\n</urlset>\n`;
}

function escapeXml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&apos;');
}
